<script setup>
// Timeline — what you lifted, one row per lift per day, newest first.
//
// This is the ONLY screen in the app that looks backwards. Ramp and Bar are
// about the next set; this is about the last six months. So it is built for
// scrolling and for one question: is the number going up. A row is a lift on a
// day — its top set, how the ramp got there, and the clips shot on the way —
// because a list of every set buried the one number anybody scans for.
//
// Filmed sets carry their thumbnail and play on tap. Unfilmed days still appear,
// because the progression is the point and a gap in it would lie about what was
// lifted; they say "not filmed" instead.
//
// THE HISTORY IS ALSO MANAGED HERE. Tapping a row opens it in place to list that
// session's sets, each removable, with the whole session (and, when the day
// holds more than one lift, the whole day) below. Every removal is immediate
// and taken back through the undo bar — no dialog (DESIGN.md). And the history
// goes out as a CSV — all of it, or a period, one lift or every lift — from the
// share glyph beside Sort.
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, onIonViewWillEnter } from '@ionic/vue';
import { AppHeader, AppLargeTitle } from '../core/ui';
import {
  attempts, attemptsReady, lifts, liftsReady, findLift, findKit,
} from '../store.js';
import { seedIfEmpty, adoptNewDefaults, currentKit } from '../lib/seed.js';
import { fmt, rangeTo } from '../lib/loading.js';
import { canFilm, resolve, play } from '../lib/liftvideo.js';
import {
  historyCsv, historyFilename, localDate, PERIODS, periodDates, selectSets,
} from '../lib/history.js';
import { forgetSets, setCount } from '../lib/forget.js';
import { shareOrDownload } from '../core/export.js';
import { toast } from '../core/util.js';

const router = useRouter();
const ready = ref(false);
const kit = ref(null);
const thumbs = ref({});     // assetId -> { status, thumbnail, durationSeconds }

// The Sort control sits on the large title's line. iOS draws that title inside
// the content (AppLargeTitle); Material has no large title and puts the name in
// the bar, so there the control takes its own line. Read the way AppLargeTitle
// reads it: Ionic stamps the mode on <html> before the first mount.
const isIos = typeof document !== 'undefined' && document.documentElement.classList.contains('ios');

onMounted(async () => {
  await Promise.all([attemptsReady, liftsReady]);
  await seedIfEmpty();
  await adoptNewDefaults();
  kit.value = currentKit();
  ready.value = true;
  if (canFilm()) loadThumbs();
});

// A tab is mounted once. Coming back after filming a set has to find its clip.
onIonViewWillEnter(() => { if (ready.value && canFilm()) loadThumbs(); });

// Resolved one at a time and cached: a library lookup is cheap but not free, and
// a timeline can hold hundreds of sets.
async function loadThumbs() {
  for (const a of attempts.value) {
    if (!a.videoAssetId || thumbs.value[a.videoAssetId]) continue;
    try {
      const r = await resolve(a.videoAssetId);
      thumbs.value = { ...thumbs.value, [a.videoAssetId]: r };
    } catch {
      // NOT 'no-access', and not 'missing': a failed question is not an answer
      // (the Film screen's rule). The plain frame stays, and a tap tries to play.
      thumbs.value = { ...thumbs.value, [a.videoAssetId]: { status: 'unknown' } };
    }
  }
}

const unit = computed(() => kit.value?.unit ?? 'kg');
const weightOf = (a) => Number(a.weight) || 0;
const nameOf = (id) => findLift(id)?.name ?? 'Lift';

// ---- dates ----------------------------------------------------------------
// "Fri 11 Sep": the device's own names for the day and month, in the design's
// order. A different year says so.
const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
function dayName(d) {
  const p = Object.fromEntries(dayFmt.formatToParts(d).map((x) => [x.type, x.value]));
  const s = `${p.weekday} ${p.day} ${p.month}`;
  return d.getFullYear() === new Date().getFullYear() ? s : `${s} ${d.getFullYear()}`;
}
function relDay(d) {
  const today = new Date();
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return '';
}
/** A group heading: "Today · Fri 11 Sep", "Tue 8 Sep". */
const dayHeading = (d) => [relDay(d), dayName(d)].filter(Boolean).join(' · ');
/** A row's own date, when rows are not grouped by day: "Today", "Tue 8 Sep". */
const dayShort = (d) => relDay(d) || dayName(d);

/** A set's time the way the phone writes one: "12:32", "7:05 pm". */
const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const timeOf = (a) => {
  const d = new Date(a.performedAt);
  return Number.isNaN(d.getTime()) ? '' : timeFmt.format(d);
};

// ---- a lift on a day --------------------------------------------------------
const liftDays = computed(() => {
  const byKey = new Map();
  for (const a of attempts.value) {
    const d = new Date(a.performedAt);
    if (!a.performedAt || Number.isNaN(d.getTime())) continue;
    const day = d.toDateString();
    const key = `${day}|${a.lift}`;
    let g = byKey.get(key);
    if (!g) { g = { key, day, date: d, lift: String(a.lift), sets: [], latest: 0 }; byKey.set(key, g); }
    g.sets.push(a);
    if (d.getTime() > g.latest) { g.latest = d.getTime(); g.date = d; }
  }
  return [...byKey.values()].map((g) => {
    // The top set is the heaviest; on a tie, the later one.
    const top = g.sets.reduce((best, a) => {
      const w = weightOf(a); const b = weightOf(best);
      if (w !== b) return w > b ? a : best;
      return new Date(a.performedAt) > new Date(best.performedAt) ? a : best;
    });
    // The clips, heaviest first: the top set's is the one people open.
    const clips = g.sets
      .filter((a) => a.videoAssetId)
      .sort((a, b) => weightOf(b) - weightOf(a) || new Date(b.performedAt) - new Date(a.performedAt));
    return {
      ...g,
      name: nameOf(g.lift),
      top,
      topWeight: weightOf(top),
      lightest: Math.min(...g.sets.map(weightOf)),
      clips,
    };
  });
});

// ---- sort and filter --------------------------------------------------------
const sort = ref('newest');           // 'newest' | 'heaviest'
const liftFilter = ref(null);         // a lift id, or null for all of them
const filmedOnly = ref(false);

/** A chip for each lift that has anything on the timeline, in the order Lifts lists them. */
const ORDER = ['squat', 'bench', 'deadlift', 'other'];
const rank = (l) => { const i = ORDER.indexOf(l.kind); return i < 0 ? ORDER.length : i; };
const liftChips = computed(() => {
  const seen = new Set(attempts.value.map((a) => String(a.lift)));
  return lifts.value
    .filter((l) => seen.has(String(l.id)))
    .sort((a, b) => rank(a) - rank(b))
    .map((l) => ({ id: String(l.id), name: l.name }));
});
// A filter on a lift that has since gone is no filter at all.
const activeLift = computed(() => (liftChips.value.some((c) => c.id === liftFilter.value) ? liftFilter.value : null));

const pickLift = (id) => { liftFilter.value = activeLift.value === id ? null : id; };
const showAll = () => { liftFilter.value = null; filmedOnly.value = false; };

const visible = computed(() => {
  const rows = liftDays.value.filter((r) => (!activeLift.value || r.lift === activeLift.value)
    && (!filmedOnly.value || r.clips.length));
  return sort.value === 'heaviest'
    ? rows.sort((a, b) => b.topWeight - a.topWeight || b.latest - a.latest)
    : rows.sort((a, b) => b.latest - a.latest);
});

/** Newest: grouped under each day. Heaviest: one run, each row with its date. */
const groups = computed(() => {
  if (sort.value === 'heaviest') return visible.value.length ? [{ key: 'all', label: '', rows: visible.value }] : [];
  const out = [];
  for (const r of visible.value) {
    let g = out[out.length - 1];
    if (!g || g.key !== r.day) { g = { key: r.day, label: dayHeading(r.date), rows: [] }; out.push(g); }
    g.rows.push(r);
  }
  return out;
});

// ---- the words on a row -------------------------------------------------------
const repsOf = (a) => {
  const n = Number(a.reps) || 0;
  const to = rangeTo(n, a.repsTo);
  return to ? `${n}–${to}` : String(n);
};

function summary(r) {
  const n = r.sets.length;
  const parts = [];
  if (sort.value === 'heaviest') parts.push(dayShort(r.date));
  parts.push(setCount(n));
  if (r.lightest < r.topWeight) parts.push(`${fmt(r.lightest)} → ${fmt(r.topWeight)}`);
  if (!r.clips.length) parts.push('not filmed');
  return parts.join(' · ');
}

const emptyFiltered = computed(() => {
  const who = activeLift.value ? nameOf(activeLift.value) : '';
  return filmedOnly.value ? `No filmed ${who ? `${who} ` : ''}sets yet.` : `No ${who} sets yet.`;
});

// ---- clips ----------------------------------------------------------------------
const thumbOf = (a) => (a.videoAssetId ? thumbs.value[a.videoAssetId] : null);
const clock = (seconds) => {
  const s = Math.round(Number(seconds));
  if (!Number.isFinite(s) || s <= 0) return '';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const filmPath = (liftId) => `/tabs/lifts/${liftId}/attempts`;

/** The lift's Film screen, where any set can be filmed or linked. */
function openLift(r) {
  if (findLift(r.lift)) router.push(filmPath(r.lift));
}

async function openClip(a) {
  // A clip that has gone, or that the photo selection leaves out, cannot play;
  // the Film screen is where the fix for both lives.
  const t = thumbOf(a);
  if (t?.status === 'missing' || t?.status === 'no-access') { openLift({ lift: a.lift }); return; }
  try { await play(a.videoAssetId); } catch (e) {
    toast(e?.code === 'unsupported' ? 'Playback needs the iOS or Android app.' : "That clip won't open.");
  }
}

// ---- a session, opened ----------------------------------------------------------
// One open at a time: the list is for scanning, and two open sessions is two
// long lists interleaved with everything between them. The key is the day and
// the lift, so a session stays open while sets come out of it, and comes back
// open after an Undo.
const openKey = ref(null);
const toggle = (r) => { openKey.value = openKey.value === r.key ? null : r.key; };
const panelId = (r) => `session-${r.key.replace(/[^A-Za-z0-9]/g, '-')}`;

/** In the order they were lifted — the ramp, read top to bottom. */
const inOrder = (r) => r.sets.slice().sort(
  (a, b) => new Date(a.performedAt) - new Date(b.performedAt) || (Number(a.id) || 0) - (Number(b.id) || 0),
);

/** The rows shown for the same day, whatever the sort: what "the whole day" removes. */
const dayRows = (r) => visible.value.filter((x) => x.day === r.day);
const daySets = (r) => dayRows(r).flatMap((x) => x.sets);

// Removing a filmed set drops the POINTER; the clip was never the app's to
// delete (lib/liftvideo.js). The undo bar says so for a single set; for a
// session the open panel has already said it.
function dropSet(a) {
  const what = `${fmt(weightOf(a))} ${unit.value} set removed`;
  forgetSets([a], a.videoAssetId ? `${what} · the clip stays in Photos.` : `${what}.`);
}
function dropSession(r) {
  forgetSets(r.sets, `Session removed · ${setCount(r.sets.length)}.`);
}
function dropDay(r) {
  const sets = daySets(r);
  forgetSets(sets, `${dayName(r.date)} removed · ${setCount(sets.length)}.`);
}

// ---- export ---------------------------------------------------------------------
// THE WHOLE TIMELINE, OR A PERIOD OF IT — never just what the chips happen to
// show. An export is a record to keep or to hand a coach, so it is chosen on its
// own terms (period, lift) in a small panel under the title, and the button says
// exactly how many sets the file will hold before anything is written.
const exportOpen = ref(false);
const period = ref('all');            // lib/history.js PERIODS
const xpLift = ref(null);             // a lift id, or null for every lift
const from = ref('');                 // a custom period, local YYYY-MM-DD
const to = ref('');
const today = ref(localDate(new Date()));

// A lift that has since left the timeline is no choice at all.
const xpActiveLift = computed(() => (liftChips.value.some((c) => c.id === xpLift.value) ? xpLift.value : null));
const range = computed(() => periodDates(period.value, { from: from.value, to: to.value }));
const chosen = computed(() => selectSets(attempts.value, { lift: xpActiveLift.value, ...range.value }));

function toggleExport() {
  exportOpen.value = !exportOpen.value;
  if (exportOpen.value) today.value = localDate(new Date());
}

function pickPeriod(id) {
  period.value = id;
  // Custom opens on a real span — the first set ever, to today — rather than on
  // two empty fields that export nothing until both are filled.
  if (id !== 'custom') return;
  if (!to.value) to.value = today.value;
  if (!from.value) {
    const first = Math.min(...attempts.value.map((a) => new Date(a.performedAt).getTime()).filter(Number.isFinite));
    from.value = localDate(new Date(first)) || today.value;
  }
}

// A lift on another kit is in that kit's unit, as the Film screen reads it.
const unitFor = (liftId) => findKit(findLift(liftId)?.kit)?.unit ?? unit.value;

const exporting = ref(false);
async function exportHistory() {
  if (exporting.value) return;
  const sets = chosen.value;
  if (!sets.length) return;
  exporting.value = true;
  try {
    const csv = historyCsv(sets, { liftName: nameOf, unitOf: (a) => unitFor(a.lift) });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const how = await shareOrDownload(blob, historyFilename(new Date(), range.value), 'Work Up history');
    // Backing out of the share sheet is a decision, not a failure: say nothing,
    // and leave the choices where they were.
    if (how !== 'aborted') {
      toast(`Exported ${setCount(sets.length)}.`);
      exportOpen.value = false;
    }
  } catch {
    toast('Could not export.');
  } finally {
    exporting.value = false;
  }
}
</script>

<template>
  <IonPage class="work-up timeline-page">
    <AppHeader title="Timeline" />

    <IonContent :fullscreen="true">
      <AppLargeTitle title="Timeline" />

      <div class="bar-extra" :class="{ ios: isIos }">
        <div v-if="ready && attempts.length" class="tools">
          <!-- A native select under a drawn label: the phone's own picker, a 44px
               target, and nothing to build for the two choices it holds. -->
          <label class="sort">
            <span class="dim" aria-hidden="true">Sort</span>
            <span aria-hidden="true">{{ sort === 'heaviest' ? 'Heaviest' : 'Newest' }}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            <select v-model="sort" aria-label="Sort the timeline">
              <option value="newest">Newest</option>
              <option value="heaviest">Heaviest</option>
            </select>
          </label>
          <!-- The share glyph, because on a phone that is what it does: the
               share sheet, with Save to Files on it. -->
          <button
            type="button"
            class="export"
            aria-label="Export history"
            :aria-expanded="exportOpen"
            aria-controls="export-panel"
            @click="toggleExport"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3.5v11" /><path d="M8.25 7.25L12 3.5l3.75 3.75" /><path d="M8.5 10H7a1.5 1.5 0 0 0-1.5 1.5v8A1.5 1.5 0 0 0 7 21h10a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 17 10h-1.5" /></svg>
          </button>
        </div>
      </div>

      <!-- THE EXPORT CHOOSER: a period, a lift, and a button that says how many
           sets the file will hold. It ignores the chips and the sort below —
           those are for looking; this is for keeping. -->
      <section v-if="exportOpen && ready && attempts.length" id="export-panel" class="xp" aria-labelledby="xp-title">
        <div class="xp-head">
          <p id="xp-title" class="serif xp-title">Export history</p>
          <button type="button" class="xp-close" aria-label="Close export" @click="exportOpen = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></svg>
          </button>
        </div>

        <p id="xp-period" class="wu-label xp-label">Period</p>
        <div class="xp-chips" role="group" aria-labelledby="xp-period">
          <button
            v-for="p in PERIODS" :key="p.id" type="button" class="wu-chip"
            :aria-pressed="period === p.id" @click="pickPeriod(p.id)"
          >{{ p.label }}</button>
        </div>
        <div v-if="period === 'custom'" class="xp-dates">
          <label class="xp-date">
            <span class="wu-label">From</span>
            <input v-model="from" type="date" :max="to || today">
          </label>
          <label class="xp-date">
            <span class="wu-label">To</span>
            <input v-model="to" type="date" :min="from" :max="today">
          </label>
        </div>

        <p id="xp-lift" class="wu-label xp-label">Lift</p>
        <div class="xp-chips" role="group" aria-labelledby="xp-lift">
          <button type="button" class="wu-chip" :aria-pressed="!xpActiveLift" @click="xpLift = null">All</button>
          <button
            v-for="c in liftChips" :key="c.id" type="button" class="wu-chip"
            :aria-pressed="xpActiveLift === c.id" @click="xpLift = c.id"
          >{{ c.name }}</button>
        </div>

        <button type="button" class="wu-btn xp-go" :disabled="!chosen.length || exporting" @click="exportHistory">
          {{ chosen.length ? `Export ${setCount(chosen.length)}` : 'Nothing to export' }}
        </button>
        <p class="xp-note">A CSV file, one row per set, for Numbers or Excel.</p>
      </section>

      <div v-if="ready && !attempts.length" class="empty">
        <p class="serif empty-title">Nothing lifted yet.</p>
        <p class="empty-body">Work up to a top set from Lifts. Every set lands here, filmed or not.</p>
      </div>

      <template v-else-if="ready">
        <div class="chips" role="group" aria-label="Show">
          <button type="button" class="wu-chip" :aria-pressed="!activeLift" @click="liftFilter = null">All</button>
          <button
            v-for="c in liftChips" :key="c.id" type="button" class="wu-chip"
            :aria-pressed="activeLift === c.id" @click="pickLift(c.id)"
          >{{ c.name }}</button>
          <button type="button" class="wu-chip" :aria-pressed="filmedOnly" @click="filmedOnly = !filmedOnly">Filmed only</button>
        </div>

        <div class="list">
          <div v-if="!groups.length" class="empty empty--filtered">
            <p class="empty-body">{{ emptyFiltered }}</p>
            <button type="button" class="wu-link" @click="showAll">Show everything</button>
          </div>

          <section v-for="g in groups" :key="g.key" class="day">
            <h2 v-if="g.label" class="wu-label daylabel">{{ g.label }}</h2>

            <article v-for="r in g.rows" :key="r.key" class="liftday" :class="{ open: openKey === r.key }">
              <button
                type="button"
                class="head"
                :aria-expanded="openKey === r.key"
                :aria-controls="panelId(r)"
                :aria-label="`${r.name}, ${fmt(r.topWeight)} ${unit} × ${repsOf(r.top)}. ${summary(r)}.`"
                @click="toggle(r)"
              >
                <span class="line1">
                  <span class="serif name">{{ r.name }}</span>
                  <span class="top"><span class="topw">{{ fmt(r.topWeight) }}</span> <span class="dim">{{ unit }} × {{ repsOf(r.top) }}</span></span>
                </span>
                <span class="line2">
                  <span class="summary">{{ summary(r) }}</span>
                  <svg class="chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                </span>
              </button>

              <div v-if="r.clips.length" class="clips">
                <div v-for="a in r.clips" :key="a.id" class="clip">
                  <button
                    type="button"
                    class="frame"
                    :class="{
                      gone: thumbOf(a)?.status === 'missing' || thumbOf(a)?.status === 'no-access',
                      pic: thumbOf(a)?.status === 'ok' && thumbOf(a)?.thumbnail,
                    }"
                    :aria-label="thumbOf(a)?.status === 'missing' ? `The ${fmt(weightOf(a))} ${unit} clip is gone from your library`
                      : thumbOf(a)?.status === 'no-access' ? `Photo access doesn't include the ${fmt(weightOf(a))} ${unit} clip`
                        : `Play the ${fmt(weightOf(a))} ${unit} clip`"
                    @click="openClip(a)"
                  >
                    <!-- Gone, or out of reach: an outline around nothing, and the word. -->
                    <template v-if="thumbOf(a)?.status === 'missing' || thumbOf(a)?.status === 'no-access'">
                      <span class="gone-word">{{ thumbOf(a).status === 'missing' ? 'Gone' : 'No access' }}</span>
                    </template>
                    <template v-else>
                      <img v-if="thumbOf(a)?.status === 'ok' && thumbOf(a)?.thumbnail" :src="thumbOf(a).thumbnail" alt="">
                      <!-- No picture (yet, or on the web): a drawn frame of a rack. -->
                      <svg v-else class="rack" viewBox="0 0 84 112" preserveAspectRatio="none" fill="none" stroke="rgba(255,255,255,0.14)" stroke-width="2" aria-hidden="true"><path d="M18 8v96M66 8v96M8 38h68" /><rect x="10" y="30" width="7" height="16" rx="1.5" fill="rgba(255,255,255,0.12)" stroke="none" /><rect x="67" y="30" width="7" height="16" rx="1.5" fill="rgba(255,255,255,0.12)" stroke="none" /><path d="M0 96h84" stroke="rgba(255,255,255,0.08)" /></svg>
                      <span class="playdot" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l10.5-6.5z" /></svg></span>
                      <span v-if="clock(thumbOf(a)?.durationSeconds)" class="dur" aria-hidden="true">{{ clock(thumbOf(a).durationSeconds) }}</span>
                    </template>
                  </button>
                  <span class="cw">{{ fmt(weightOf(a)) }} {{ unit }}</span>
                </div>
              </div>

              <!-- THE SESSION, OPENED: every set, each removable; then the whole
                   session, the whole day, and the way to Film. -->
              <div v-if="openKey === r.key" :id="panelId(r)" class="session">
                <ul class="sets" :aria-label="`${r.name} sets, ${dayName(r.date)}`">
                  <li v-for="a in inOrder(r)" :key="a.id" class="set">
                    <span class="setw"><span class="topw">{{ fmt(weightOf(a)) }}</span> <span class="dim">{{ unit }} × {{ repsOf(a) }}</span></span>
                    <span class="setmeta">{{ timeOf(a) }} · {{ a.videoAssetId ? 'filmed' : 'not filmed' }}</span>
                    <button
                      type="button"
                      class="drop"
                      :aria-label="`Remove the ${fmt(weightOf(a))} ${unit} set at ${timeOf(a)}`"
                      @click="dropSet(a)"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></svg>
                    </button>
                  </li>
                </ul>
                <p v-if="r.clips.length" class="keep">Clips stay in your Photos.</p>
                <!-- Removals down the left, the way to Film on the right. -->
                <div class="acts">
                  <button type="button" class="wu-link" @click="dropSession(r)">Remove this session</button>
                  <button v-if="findLift(r.lift)" type="button" class="wu-link film" @click="openLift(r)">Open Film</button>
                  <button
                    v-if="dayRows(r).length > 1"
                    type="button"
                    class="wu-link whole-day"
                    :aria-label="`Remove everything on ${dayName(r.date)}: ${dayRows(r).length} lifts, ${setCount(daySets(r).length)}`"
                    @click="dropDay(r)"
                  >Remove the whole day</button>
                </div>
              </div>
            </article>
          </section>
        </div>
      </template>
    </IonContent>
  </IonPage>
</template>

<style scoped>
/* The drawn clip frame, from screens.py `thumb()`: its own two greys per scheme,
   because a video frame is dark in both. */
.timeline-page { --thumb: #2A2D30; --thumb2: #3A3E42; }
@media (prefers-color-scheme: dark) {
  .timeline-page { --thumb: #23272B; --thumb2: #31363B; }
}

/* ---- Sort and Export, on the title's line ---- */
.bar-extra { display: flex; justify-content: flex-end; padding: 0 var(--wu-pad); }
/* iOS: pulled up beside the large title, which is a 52px header in the content.
   Above it, too: the large title's toolbar spans the full width at z-index 9,
   and at z-index 1 it took every tap meant for Sort and Export. */
.bar-extra.ios { height: 0; position: relative; z-index: 10; }
.bar-extra.ios .tools { position: absolute; right: var(--wu-pad); bottom: 4px; }

.tools { display: flex; align-items: center; gap: 6px; }

.sort {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  color: var(--ink);
  font: 400 0.8125rem/1 var(--face-mono);
  cursor: pointer;
}
.sort .dim { margin-right: 2px; }
.sort select {
  position: absolute;
  inset: 0;
  width: 100%;
  opacity: 0;
  font-size: 16px; /* under 16px, iOS zooms the page on focus */
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}
.sort:focus-within { outline: 2px solid var(--ink); outline-offset: 3px; border-radius: 6px; }

/* A 44px target; the glyph's right edge sits on the page's edge, like Sort's. */
.export {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0 -12px 0 0;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: none;
  color: var(--ink);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.export:active,
.export[aria-expanded='true'] { background: var(--seg); }

/* ---- the export chooser ----
 * A card, because it is a temporary thing laid over the timeline's own
 * controls and has to read as separate from the filter chips under it. Field
 * ground, the field line, the big field's radius; chips and a primary button
 * straight from the shared classes. */
.xp {
  margin: 12px var(--wu-pad) 0;
  padding: 10px 20px 20px;
  border: 1px solid var(--field-line);
  border-radius: 18px;
  background: var(--field);
}
.xp-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.xp-title { margin: 0; font-size: 24px; line-height: 1; }
.xp-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 44px;
  height: 44px;
  margin-right: -14px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: none;
  color: var(--dim);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.xp-close:active { background: var(--seg); color: var(--ink); }
.xp-label { margin: 12px 0 10px; }
.xp-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.xp-dates { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 14px; }
.xp-date { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.xp-date input {
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid var(--field-line);
  border-radius: 12px;
  background: var(--bg);
  color: var(--ink);
  font: 500 0.9375rem/1 var(--face-mono);
  font-variant-numeric: tabular-nums;
  -webkit-appearance: none;
  appearance: none;
}
.xp-date input:focus { border-color: var(--ink); box-shadow: 0 0 0 1px var(--ink); outline: none; }
.xp-go { display: flex; width: 100%; margin-top: 22px; }
.xp-note { margin: 10px 0 0; font: 400 0.75rem/1.5 var(--face-mono); color: var(--dim); }

/* ---- the chips: one row, scrolling sideways under the title ---- */
.chips {
  display: flex;
  gap: 8px;
  padding: 19px var(--wu-pad) 2px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.chips::-webkit-scrollbar { display: none; }

/* ---- the list ---- */
.list { padding: 18px var(--wu-pad) 0; }

.day + .day { margin-top: 18px; }
.daylabel { margin: 0 0 4px; }

.liftday {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  border-top: 1px solid var(--line);
}

.head {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  font: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.head:active .name { color: var(--dim); }

.line1 { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.name { font-size: 24px; line-height: 1; min-width: 0; }
.top { flex: none; font: 400 0.9375rem/1 var(--face-mono); font-variant-numeric: tabular-nums; }
.topw { font-weight: 500; }
.line2 { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.summary { font: 400 0.75rem/1.3 var(--face-mono); color: var(--dim); }
/* Says the row opens; turns over when it has. */
.chev { flex: none; color: var(--faint); transition: transform var(--dur) var(--ease); }
.liftday.open .chev { transform: rotate(180deg); color: var(--dim); }

/* ---- the clips ---- */
.clips {
  display: flex;
  gap: 10px;
  margin-right: calc(-1 * var(--wu-pad));
  padding: 4px var(--wu-pad) 0 0;
  overflow-x: auto;
  scrollbar-width: none;
}
.clips::-webkit-scrollbar { display: none; }

.clip { flex: none; display: flex; flex-direction: column; gap: 6px; }

.frame {
  position: relative;
  display: block;
  width: 84px;
  height: 112px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(180deg, var(--thumb2) 0%, var(--thumb) 100%);
  color: #F1F1EE;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.frame:active { opacity: 0.85; }
.frame img, .frame .rack { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }

.playdot {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 34px;
  height: 34px;
  margin: -17px 0 0 -17px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
}
.frame.pic .playdot { background: rgba(0, 0, 0, 0.35); }

.dur {
  position: absolute;
  right: 7px;
  bottom: 6px;
  font: 400 10px/1 var(--face-mono);
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.85);
}
.frame.pic .dur { text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6); }

/* GONE, SAID WITH A DASH: an outline around nothing, never a colour. */
.frame.gone {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px dashed var(--faint);
  background: transparent;
  color: var(--dim);
}
.gone-word { font: 400 11px/1.2 var(--face-mono); text-align: center; padding: 0 6px; }

.cw { font: 400 0.75rem/1 var(--face-mono); color: var(--dim); font-variant-numeric: tabular-nums; }

/* ---- a session, opened ----
 * Hairline rows one step quieter than the lift's: the weight in the row's mono,
 * the time and "filmed" dim, the remove a drawn ✕ with a 44px target whose
 * glyph lines up with the top set's right edge. */
.session { display: flex; flex-direction: column; margin-top: 4px; }
.sets { list-style: none; margin: 0; padding: 0; }
.set {
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr) 44px;
  align-items: center;
  column-gap: 14px;
  min-height: 48px;
  border-top: 1px solid var(--line);
}
.setw {
  font: 400 0.875rem/1 var(--face-mono);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.setmeta {
  min-width: 0;
  font: 400 0.75rem/1.3 var(--face-mono);
  color: var(--dim);
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.drop {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  /* A grid item ignores a negative right margin; shifting it by its left one
     puts the glyph's edge on the top set's edge. */
  margin: 0 -16px 0 16px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: none;
  color: var(--dim);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.drop:active { background: var(--seg); color: var(--ink); }

.keep {
  margin: 0;
  padding: 12px 0 0;
  border-top: 1px solid var(--line);
  font: 400 0.75rem/1.5 var(--face-mono);
  color: var(--dim);
}
.acts {
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  column-gap: 20px;
  padding-top: 4px;
}
.acts .wu-link { justify-self: start; }
.acts .film { justify-self: end; }
.acts .whole-day { grid-column: 1; }
.keep + .acts { padding-top: 0; }
.sets + .acts { border-top: 1px solid var(--line); }

/* ---- empty ---- */
.empty { padding: 28px var(--wu-pad) 0; }
.empty--filtered { padding: 8px 0 0; }
.empty-title { margin: 0 0 8px; font-size: 24px; line-height: 1.1; }
.empty-body { margin: 0; max-width: 42ch; font: 400 0.8125rem/1.6 var(--face-mono); color: var(--dim); }
.empty--filtered .wu-link { margin-top: 4px; }
</style>
